import React, { useMemo, useRef, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useItems } from 'hooks/atoms/items-hook';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Select, MenuItem, FormControl, InputLabel } from '@material-ui/core';
import CollectionKeys from 'hooks/molecules/collection-keys';
import { uploadTemplate } from '../../../../http/upload-assets-client';
import AppButtonMain from '../../../basic/AppButtonMain';
import axiosClient from '../../../../http/axios-client';
import { useAlerts } from '../../../../hooks/alerts-hooks';

const ThematicPackageReportTemplateUpload = (
  {
    id,
    reason,
  },
) => {
  const formRef = useRef();

  const [ selectedFile, setSelectedFile ] = useState(null);

  const [ localeStr, setLocaleStr ] = useState(2);

  const [ region, setRegion ] = useState('EU');

  const {
    // eslint-disable-next-line no-unused-vars
    items: locales,
    itemsLoading: localesLoading,
  } = useItems(CollectionKeys.LOCALES);

  const {
    data: template,
    isLoading: templateIsLoading,
    isIdle: templateIsIdle,
  } = useQuery(
    [ 'templates', { thematicPackage: id, reason }, localeStr, region ],
    () => {
      return axiosClient.get(`templates?locale=${ localeStr }&country_region=${ region }`, {
        params: {
          thematic_package: id,
          reason,
          _sort: `${ 'published_at' }:DESC`,
        },
      }).then((data) => {
        if (!data || !data.length) {
          return null;
        }
        return data[0];
      });
    },
    {
      onError() {
        alertError('Something went wrong. Please try again.');
      },
    },
  );

  const { alertSuccess, alertError } = useAlerts();

  const queryClient = useQueryClient();

  const {
    mutateAsync: createTemplate,
    isLoading: templateIsCreating,
  } = useMutation(
    [ 'templates', 'create' ],
    ({
      formData,
    }) => {
      return Promise.resolve()
        .then(() => {
          return uploadTemplate(formData);
        })
        .then((data) => {
          if (!data || !data.id) {
            throw new Error('Failed to fetch correct template data');
          }
          return axiosClient.put(`templates/${ data.id }`, {
            thematic_package: id,
            reason,
            locale: localeStr,
            country_region: region,
          });
        });
    }, {
      onSuccess() {
        setSelectedFile(null);
        alertSuccess('Template was uploaded successfully');
      },
      onError() {
        alertError('Something went wrong. Please try again.');
      },
      onSettled() {
        return queryClient.invalidateQueries([ 'templates' ]);
      },
    },
  );

  const uploadIsDisabled = useMemo(() => {
    return !selectedFile || templateIsCreating;
  }, [ selectedFile, templateIsCreating ]);

  const {
    mutateAsync: deleteTemplate,
    isLoading: templateIsDeleting,
  } = useMutation(
    [ 'templates', 'delete' ],
    (t) => {
      return axiosClient.delete(`templates/${ t.id }`);
    },
    {
      onSettled() {
        return queryClient.invalidateQueries([ 'templates' ]);
      },
    },
  );

  return (
    <Grid container item xs={ 12 }>
      {
        (templateIsLoading || templateIsIdle || localesLoading) ? <CircularProgress /> : (
          <>
            <Grid item xs={ 12 }>
              <FormControl>
                <InputLabel id='demo-simple-select-label'>Locale</InputLabel>
                <Select
                  variant='standard'
                  label='Locale'
                  value={ localeStr }
                  onChange={ (e) => {
                    setLocaleStr(e.target.value);
                  } }
                >
                  {
                    locales?.map((locale) => {
                      return (
                        <MenuItem
                          key={ locale.id }
                          value={ locale.id }
                        >
                          {locale.name}
                        </MenuItem>
                      );
                    })
                  }
                </Select>
              </FormControl>

              <FormControl style={ { marginLeft: '50px' } }>
                <InputLabel id='demo-simple-select-label-region'>Region</InputLabel>
                <Select
                  variant='standard'
                  label='Region'
                  value={ region }
                  onChange={ (e) => {
                    setRegion(e.target.value);
                  } }
                >
                  <MenuItem
                    value='EU'
                  >
                    EU
                  </MenuItem>

                  <MenuItem
                    value='US'
                  >
                    US
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={ 12 } style={ { margin: '12px 0px' } }>
              {
                !template?.id ? (
                  <form ref={ formRef }>
                    <input
                      type='file'
                      name='file'
                      accept='.doc,.docx'
                      onChange={ (e) => {
                        return setSelectedFile(e.target.value);
                      } }
                    />
                  </form>
                ) : (
                  <>
                    <p>
                      Uploaded template:
                      {template.location}
                    </p>
                  </>
                )
              }
            </Grid>
            <Grid item xs={ 12 } style={ { margin: '12px 0px' } }>
              {
                !template?.id ? (
                  <AppButtonMain
                    text={ templateIsCreating ? 'Uploading' : 'Upload' }
                    disabled={ uploadIsDisabled }
                    onClick={ () => {
                      const formData = new FormData(formRef.current);
                      return createTemplate({ formData });
                    } }
                  />
                ) : (
                  <AppButtonMain
                    color='#ae0b0b'
                    text={ templateIsDeleting ? 'Deleting' : 'Delete' }
                    disabled={ templateIsDeleting || !template?.id }
                    onClick={ () => { return deleteTemplate(template); } }
                  />
                )
              }
            </Grid>
          </>
        )
      }
    </Grid>
  );
};

export default ThematicPackageReportTemplateUpload;
